import { OutboxStatus } from 'src/app/core/offline/offline.models';
import { ConflictField } from 'src/app/core/offline/outbox-handler';

// Offline E2 step 3 (docs/plans/2026-10-11-offline-e2-schritt3-plan.md): types of the task offline mode.

export type TaskStatus = 'To Do' | 'In Progress' | 'Finished';

export interface TaskBkp {
  number: string;
  description: string;
}

export interface TaskLocationPart {
  number: string | null;
  name: string | null;
}

export interface TaskLocation {
  building?: TaskLocationPart | null;
  floor?: TaskLocationPart | null;
  unit?: TaskLocationPart | null;
  room?: TaskLocationPart | null;
}

/** the form of a new task; deadlineDate is the ISO value of toDeadlineIso, fixed when saving (plan D5) */
export interface TaskCreateFields {
  note: string;
  status: TaskStatus;
  bkp: TaskBkp;
  location: TaskLocation;
  deadlineDate?: string | null;
  /** E-4: capture time of «Erledigt» (ISO); the backend takes it only from the outbox, in [now − 7 days, now + 5 min], capped to now */
  completionDate?: string;
  /** E-4 follow-up: capture time of the new task (ISO), same rule as completionDate; set by createTask, never by the form */
  creationDate?: string;
}

/** the fields one action sets («Felder statt Diff»): never a field the user did not touch (R2 of step 1) */
export interface TaskFields {
  note?: string;
  status?: TaskStatus;
  bkp?: TaskBkp;
  location?: TaskLocation;
  deadlineDate?: string | null;
  /** E-4: set with status «Finished» only (withCompletionDate), never by the user */
  completionDate?: string;
}

/** task.create; journalName/journalType only for the views (never sent) */
export interface TaskCreatePayload {
  projectId: string;
  journalId: string;
  fields: TaskCreateFields;
  journalName?: string | null;
  journalType?: string | null;
}

/** task.update: the set fields, ifVersion as precondition (spec §4.2); label only for banner and dialog */
export interface TaskUpdatePayload {
  /** server id, or local:… of a task created offline (the engine replaces it before sending) */
  taskId: string;
  ifVersion?: number;
  fields: TaskFields;
  label?: string;
}

export interface TaskDeletePayload {
  taskId: string;
  label?: string;
  /** E-3: the version the delete expects (outbox only); 409 VERSION_CONFLICT → conflict dialog */
  ifVersion?: number;
}

/** task.photo: one picture; the file lives in the blobs table (blobIds set by OfflineStore.enqueue) */
export interface TaskPhotoPayload {
  taskId: string;
  fileName: string;
  label?: string;
  blobIds?: string[];
}

export interface TaskCommentPayload {
  taskId: string;
  text: string;
  label?: string;
}

/** a task in the offline snapshot (backend OfflineSnapshotService.toSnapshotTask) */
export interface SnapshotTask {
  id: string;
  journalId: string;
  journalName: string | null;
  journalType: string | null;
  bkp: TaskBkp | null;
  location: TaskLocation | null;
  /** null together with noteTruncated true: a very long note is not in the snapshot (E2 gate R4) */
  note: string | null;
  noteTruncated?: boolean;
  status: TaskStatus;
  deadlineDate: string | null;
  completionDate: string | null;
  creationDate: string;
  userId: string;
  hasAssignedMember: boolean;
  isNew: boolean;
  version: number;
  pictureCount: number;
}

export interface SnapshotTaskJournal {
  id: string;
  name: string | null;
  type: string;
  isOwn: boolean;
  /** BE-c (Revision 1): journal date, shown by the journal lists offline */
  date?: string | null;
  /** BE-c (Revision 1, FE-B1): the fixed BKP of a defect journal; missing for other journals */
  bkp?: TaskBkp | null;
  /** closing time of a Werksabnahme (defectJournal), null when open; missing in older snapshots */
  closedAt?: string | null;
}

/** a task journal of a project on this device (journal lists, new task and prefill offline, Kanban filters) */
export interface TaskJournalView {
  id: string;
  name: string | null;
  type: string;
  projectId: string;
  date: string | null;
  bkp: TaskBkp | null;
  /** closing time of a Werksabnahme, null when open */
  closedAt: string | null;
}

/** projects and journals for the Kanban filters and the new-task form while offline */
export interface TaskFilterInfo {
  projects: { id: string; projectName: string }[];
  journals: TaskJournalView[];
}

/** BE-c: comments of the visible tasks, guests by name only */
export interface SnapshotComment {
  id: string;
  taskId: string;
  text: string;
  truncated?: boolean;
  creationDate: string;
  authorName: string;
  companyName: string | null;
  isOwn: boolean;
  guest?: boolean;
}

export interface CommentLimits {
  perTask: number;
  total: number;
  allTruncated: boolean;
  truncatedTaskIds: string[];
}

/** guests have id null and guest true: never key rows by id (BE-b ruling M1) */
export interface SnapshotMember {
  id: string | null;
  guest?: boolean;
  /** the user of an internal member (NA-02, BE always sets it for internal members); null for guests, missing in older snapshots */
  userId?: string | null;
  type: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  bkps: string[];
  status: string | null;
}

export interface TaskEntities {
  buildingList: string[];
  floorList: string[];
  unitList: string[];
  roomList: string[];
  bkpList: TaskBkp[];
}

/** the parts of GET /projects/:id/offline-snapshot the task pages read; comments/entities since BE-c (may be missing) */
export interface TaskSnapshotData {
  project: { id: string; projectName: string; organizationId: string };
  me: { userId: string; bkps: string[] };
  taskJournals: SnapshotTaskJournal[];
  tasks: SnapshotTask[];
  members: SnapshotMember[];
  comments?: SnapshotComment[];
  commentLimits?: CommentLimits;
  entities?: TaskEntities;
}

/** a photo saved on this device, waiting for its upload */
export interface LocalTaskPhoto {
  opId: string;
  blobId: string;
  fileName: string;
  status: OutboxStatus;
  /** its request went out at least once: maybe on the server already, no removal (plan D9) */
  sent: boolean;
}

/** a task as Kanban, details and gallery show it: server or snapshot data with the outbox on top (plan C1) */
export interface TaskView {
  /** server id, or local:… while the task exists only on this device */
  id: string;
  projectId: string;
  journalId: string;
  journalName: string | null;
  journalType: string | null;
  bkp: TaskBkp | null;
  location: TaskLocation | null;
  note: string;
  /** device view only: the note is not on this device (empty text), editing it needs the network (E2 gate R4) */
  noteTruncated?: boolean;
  status: TaskStatus;
  deadlineDate: string | null;
  completionDate: string | null;
  creationDate: string;
  userId: string;
  isNew: boolean;
  hasAssignedMember: boolean;
  version: number;
  newCommentsCount: number;
  pictures: unknown[];
  taskStatusLogs: unknown[];
  sharedLinkId?: string | null;
  localId: string | null;
  /** has outbox entries (any task.* type) */
  pending: boolean;
  /** one of its entries is being sent right now */
  uploading: boolean;
  /** its create was sent at least once (maybe on the server already): no discard */
  sent: boolean;
  /** first problem of its entries (conflict, failed, blocked) */
  problem: OutboxStatus | null;
  /** existing Kanban marker (orange border) for a task that is not on the server yet */
  notSynced: boolean;
  /** pictures on the server this device cannot show offline (spec §3) */
  offlinePictureCount: number;
  localPhotos: LocalTaskPhoto[];
  /** the remaining fields of the server DTO (user, documentPin, pinHistory …) stay as they are */
  [key: string]: unknown;
}

export interface TaskList {
  tasks: TaskView[];
  /** false: no project of the request is on this device (no snapshot); local tasks are still listed */
  available: boolean;
}

export interface TaskLoad {
  view: TaskView;
  /** server version the view starts from (API or snapshot), base of ifVersion; null for a local task */
  server: TaskView | null;
}

/** a comment as the lists show it; the shape of the server DTO the templates read (user, userId, creationDate) */
export interface CommentView {
  /** server id, or the opId of a comment waiting in the outbox */
  id: string;
  taskId: string;
  text: string;
  creationDate: string;
  userId: string | null;
  user: { firstName: string | null; lastName: string | null; organization: { name: string | null } | null };
  isNew: boolean;
  truncated: boolean;
  local: boolean;
  pending: boolean;
  problem: OutboxStatus | null;
  [key: string]: unknown;
}

export interface CommentList {
  /** newest first, like GET /tasks/:id/comments */
  comments: CommentView[];
  /** offline from the snapshot: older comments of this task may only be visible online */
  olderOnlyOnline: boolean;
}

export interface TaskTarget {
  projectId: string;
  journalId: string;
  journalName?: string | null;
  journalType?: string | null;
}

export interface TaskRef {
  projectId: string;
  /** server id or local:… */
  taskId: string;
  /** version the page shows (base of ifVersion offline) */
  version?: number | null;
  /** short text for banner and dialog */
  label?: string;
  /** the page shows the device view of a task whose note is not on the device (E2 gate R4): a note is never sent */
  noteTruncated?: boolean;
}

export interface TaskWriteResult {
  /** local:… or the server id */
  taskId: string;
  /** true: at least one part waits in the outbox */
  queued: boolean;
  /** photos that did not fit on the device (spec §6); they stay in the form */
  rejectedPhotos: File[];
  /** photos the server refused online (4xx): the page marks their drafts failed (Q1) */
  failedPhotos: { file: File; message: string }[];
}

export interface TaskDeleteResult {
  queued: boolean;
  /** a task only on this device, never sent: dropped with its photos and comments, no request */
  discarded: boolean;
}

export interface TaskCommentResult {
  queued: boolean;
}

/** E-1 (Security-Gate E2): the backend takes at most 10 000 characters per comment, members and guests */
export const COMMENT_MAX_LENGTH = 10_000;
/** the counter under the comment field shows from this length on (90 %) */
export const COMMENT_COUNTER_FROM = 9_000;

/** a comment longer than COMMENT_MAX_LENGTH: refused before it is queued or sent (a queued one would fail for good) */
export class CommentTooLongError extends Error {
  constructor() {
    super('comment too long');
    this.name = 'CommentTooLongError';
  }
}

export interface TaskCreated {
  projectId: string;
  localId: string;
  serverId: string;
}

/** «Selbst zusammenführen» (spec §4.1, plan D11) */
export interface TaskMergeLoad {
  opId: string;
  current: TaskView;
  mine: TaskFields;
  /** my fields that differ from the newer version */
  diffs: ConflictField[];
}

export type TaskMergeChoices = Partial<Record<'status' | 'note' | 'bkp' | 'location' | 'deadlineDate', 'mine' | 'theirs'>>;
